import { useEventStore } from '@/state/stores/use-event-store.ts';
import { type ProductKey } from '@/types/dtos/orders/response/product.dto.ts';
import EventCatalogProductDetails from '@/components/events/event-catalog/event-catalog-product-details.tsx';
import { useGetEventOrdersQuery } from '@/state/queries/orders/use-get-event-orders.ts';
import EventCatalogProductsListModal from '@/components/events/event-catalog/event-catalog-products-list.modal.tsx';
import EventCatalogOrderCheckoutModal from '@/components/events/event-catalog/event-catalog-order-checkout.modal.tsx';
import { useState } from 'react';
import { getOriginEventId } from '@/helpers/event.helper';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

export default function EventCatalogProduct({ productKey }: { productKey: ProductKey }) {
  const [isCatalogPickerOpen, setIsCatalogPickerOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [restoreCheckoutWhenPickerCloses, setRestoreCheckoutWhenPickerCloses] = useState(false);
  const event = useEventStore((s) => s.event);
  const { data: orders } = useGetEventOrdersQuery(getOriginEventId(event));
  const { hasAccess, isLoading } = useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);

  const handleOpenCatalogPicker = () => {
    setIsCatalogPickerOpen(true);
    setIsCheckoutOpen(false);
  };

  const handleCheckoutRequested = () => {
    setIsCheckoutOpen(true);
    setRestoreCheckoutWhenPickerCloses(true);
    setIsCatalogPickerOpen(false);
  };

  const handleCatalogPickerClose = () => {
    setIsCatalogPickerOpen(false);
    setIsCheckoutOpen(restoreCheckoutWhenPickerCloses);
  };

  const handleCheckoutClose = () => {
    setIsCheckoutOpen(false);
    setRestoreCheckoutWhenPickerCloses(false);
  };

  if (isLoading || !hasAccess) {
    return null;
  }

  return (
    <>
      <EventCatalogProductDetails
        productKey={productKey}
        event={event}
        orders={orders}
        onCheckoutRequested={handleCheckoutRequested}
      />
      <EventCatalogProductsListModal
        event={event}
        open={isCatalogPickerOpen}
        expandedProductKey={productKey}
        onCheckoutRequested={handleCheckoutRequested}
        onModalClose={handleCatalogPickerClose}
      />
      <EventCatalogOrderCheckoutModal
        open={isCheckoutOpen}
        event={event}
        onOpenCatalogPicker={handleOpenCatalogPicker}
        onModalClose={handleCheckoutClose}
      />
    </>
  );
}
